// SEARCH THE CONTRACTOR HOURS REPORT, ACROSS EVERY COLUMN.
//
// Owner, 2026-10-22: "add a search input to search in the filtered or
// unfiltered list of items, on the contractor and cost, make sure it looks
// in all columns and uses all columns value to match the search term".
//
// THE TABLE IS TWO LEVELS, so the search is too: a contractor row, and the
// LINES underneath it - the issues and shop drawings the hours were recorded
// against. Searching only the top level would make "SPX-412" find nothing,
// and that is the search people actually type.
//
// WHAT "ALL COLUMNS" MEANS HERE: every value the row SHOWS, including the
// ones a render function makes out of other fields - the item's code and
// title, the project, the people, the rate's source, the dates - not just
// the fields that happen to have a dataIndex. A column whose text is
// assembled at render time is still a column someone reads and will type
// from.
//
// IT USES THE APP'S ONE SEARCH RULE (components/SearchSelect.matchText):
// every typed word must appear somewhere, in any order, accents and
// punctuation folded, and a bare number has to match a whole word so that
// typing "2" does not match every date in 2026.
import { matchText } from "components/SearchSelect/searchSelect";

const txt = (v) => (v == null ? "" : String(v));
const money = (v) => (typeof v === "number" ? v.toFixed(2) : "");

/** Everything one LINE shows, as one string. */
export function lineText(r) {
  if (!r) return "";
  const item = r.item || {};
  const dates = r.dates || {};
  return [
    r.system, r.companyName, r.companyId, r.person, r.detail,
    item.label, item.title, item.kind, item.id,
    r.projectTitle, r.projectId,
    r.createdBy, r.assignedTo,
    // the numbers as they are printed, so "200.00" and "5" both find it
    txt(r.hours), txt(r.rate), money(r.cost), money(r.overrideCost), txt(r.overrideRate),
    r.rateSource,
    // the flags a reader can see
    r.noCost ? "no cost" : "", r.unidentified && r.unidentified.length ? "unattributed" : "",
    dates.opened, dates.closed, dates.approved, r.at,
  ].filter(Boolean).map(txt).join(" ");
}

/** Everything a CONTRACTOR row shows, not counting its lines. */
export function contractorText(c) {
  if (!c) return "";
  const sys = Object.entries(c.systems || {})
    .map(([k, v]) => `${k} ${v && v.hours != null ? v.hours : ""}`)
    .join(" ");
  return [
    c.companyName, c.companyId,
    txt(c.hours), money(c.cost), money(c.recordedCost),
    c.noCostRows ? "no cost" : "",
    c.unidentified && c.unidentified.length ? "unattributed" : "",
    sys,
  ].filter(Boolean).map(txt).join(" ");
}

/**
 * The report, narrowed to a search term.
 *
 * A contractor whose OWN row matches is kept whole - you searched for them,
 * you want their work. A contractor kept only because some of its lines
 * matched shows just those lines, because showing the rest would bury the
 * thing that was found.
 *
 * Returns { contractors, matchedLines, hiddenContractors } so the page can
 * SAY what the search did rather than silently showing less. The totals
 * above the table are the report's, not the search's, and the page says so:
 * a search that quietly re-totalled the page would be a different number
 * under the same heading.
 */
export function searchReport(contractors, term) {
  const list = contractors || [];
  if (!String(term || "").trim()) {
    return { contractors: list, matchedLines: null, hiddenContractors: 0, searching: false };
  }
  const out = [];
  let matchedLines = 0;
  for (const c of list) {
    if (matchText(term, contractorText(c))) {
      out.push(c);
      matchedLines += (c.rows || []).length;
      continue;
    }
    const rows = (c.rows || []).filter((r) => matchText(term, lineText(r)));
    if (rows.length) {
      out.push({ ...c, rows });
      matchedLines += rows.length;
    }
  }
  return {
    contractors: out,
    matchedLines,
    hiddenContractors: list.length - out.length,
    searching: true,
  };
}
